import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Iter76 · Silently refresh the access_token on 401 so a user whose 12-hour JWT
// expired mid-session doesn't see "Not authenticated" when applying rep status
// or editing a roster. The 7-day refresh_token cookie covers the gap.
// Guarantees:
//   · at most ONE /auth/refresh in flight at a time (queued retries)
//   · never retries /auth/* endpoints (prevents refresh loops)
//   · falls through to the original 401 if the refresh itself fails
let _refreshInFlight = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";
    if (
      status !== 401 ||
      !original ||
      original._authRetried ||
      url.includes("/auth/login") ||
      url.includes("/auth/register") ||
      url.includes("/auth/refresh") ||
      url.includes("/auth/logout")
    ) {
      throw error;
    }
    original._authRetried = true;
    try {
      if (!_refreshInFlight) {
        _refreshInFlight = api.post("/auth/refresh").finally(() => {
          _refreshInFlight = null;
        });
      }
      await _refreshInFlight;
      return api.request(original);
    } catch (refreshErr) {
      throw error;
    }
  },
);

/**
 * Upload a FormData body (files, images, PDFs, CSVs). Sets Content-Type to `undefined`
 * so axios strips the default application/json header and lets the browser generate
 * the correct `multipart/form-data; boundary=...` header from the FormData payload.
 *
 * Never override this Content-Type manually — a boundary-less multipart header breaks
 * FastAPI's parser and the upload will silently return zero fields.
 */
export const apiUpload = (url, formData, options = {}) =>
  api.post(url, formData, {
    ...options,
    headers: { ...(options.headers || {}), "Content-Type": undefined },
  });

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
